// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { RefObject, useMemo } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';

import { EditCalendar, RepeatConfirm, useAvailabilityPopover } from './hooks';

import Popover from 'presentations/views/components/atoms/Popover';
import { ViewerItem } from 'presentations/api/schema/user';
import Modal from 'presentations/views/components/atoms/Modal';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import {
  gray01,
  gray04,
  gray05,
  gray06,
  white,
} from 'presentations/views/styles/color';
import {
  fontSize,
  fontWeight,
  forceLayoutTheme,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import DateTimeSelect from 'presentations/views/components/molecules/DateTimeSelect';
import Select from 'presentations/views/components/atoms/Select';
import RoutineScheduleForm from 'presentations/views/components/organisms/RoutineScheduleForm';
import Button from 'presentations/views/components/atoms/Button';
import FlowSettingMenu, {
  MenuOptions,
} from 'presentations/views/components/molecules/FloatSettingMenu';
import { EventDate } from 'presentations/reducers/DashboardCalendar';
import Dialog from 'presentations/views/components/atoms/Dialog';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import Radio from 'presentations/views/components/atoms/Radio';
import { FrequencyType } from 'domains/models/event_type_calendar/frequencyType';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';

interface Props {
  viewer: ViewerItem;
  eventTypeId: number | null;
  templateId: number | null;
  timeZone: string;
  calendarTimeZone: string;
  anchor?: HTMLElement;
  editCalendar: EditCalendar | null;
  isLeftPosition?: boolean;
  calendarContainerRef?: RefObject<HTMLElement> | null;
  selectedEventDate: EventDate | null;
  viewMode: 'calendar' | 'list';
  onClose: Function;
  onUpdatedCalendar?: Function;
  intervalMinutes: number | null;
  onChangeDateByForm?: (eventDate: EventDate) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions, routineScheduleForm } = useAvailabilityPopover(props);

  const repeatOptions = [
    { text: t('Repeat no'), value: 0 },
    { text: t('Repeat yes'), value: 1 },
  ];
  const endDateOptions = [
    { text: t('End date none'), value: 0 },
    { text: t('End date specified'), value: 1 },
  ];

  const settingMenus: MenuOptions[] = [
    {
      icon: 'delete',
      label: t('delete'),
    },
  ];

  const ConfirmDeleteDialogComponent = useMemo(() => {
    if (!state.isConfirmDelete) return null;

    return (
      <ConfirmationDialog
        isOpen
        title={t('Confirm Delete')}
        isLoading={state.isLoading}
        onSubmit={actions.onSubmitConfirmDelete}
        onCancel={actions.onCloseConfirmDelete}
        submitLabel={t('delete')}
        cancelLabel={t('Cancel')}
      />
    );
  }, [
    state.isConfirmDelete,
    state.isLoading,
    actions.onSubmitConfirmDelete,
    actions.onCloseConfirmDelete,
  ]);

  const RepeatConfirmDialogComponent = useMemo(() => {
    if (!state.repeatConfirmType) return null;

    // リストの削除なら削除確認だけする
    if (
      props.viewMode === 'list' &&
      state.repeatConfirmType.method === 'delete'
    ) {
      return (
        <ConfirmationDialog
          isOpen
          title={t('Confirm Delete')}
          isLoading={state.isLoading}
          onSubmit={actions.onSubmitRepeatConfirm}
          onCancel={actions.onCloseRepeatConfirm}
          submitLabel={t('delete')}
          cancelLabel={t('Cancel')}
        />
      );
    }

    let onlyThisDayText: string;
    let allText: string;

    switch (props.editCalendar?.calendar.frequencyType) {
      case FrequencyType.Monthly:
        onlyThisDayText = t(`Repeat Confirm Only This Month`, {
          method: t(state.repeatConfirmType.method),
        });
        allText = t(`Repeat Confirm Every Month`);
        break;
      case FrequencyType.Weekly:
        onlyThisDayText = t(`Repeat Confirm Only This Week`, {
          method: t(state.repeatConfirmType.method),
        });
        allText = t(`Repeat Confirm Every Week`);
        break;
      case FrequencyType.Daily:
      default:
        onlyThisDayText = t(`Repeat Confirm Only This Day`, {
          method: t(state.repeatConfirmType.method),
        });
        allText = t(`Repeat Confirm Every Day`);
        break;
    }

    return (
      <Dialog
        isOpen
        title={t('Repeat Confirm Title', {
          method: t(state.repeatConfirmType.method),
        })}
        onClose={actions.onCloseRepeatConfirm}
        width={400}
        isFillWidth
        actions={
          <DialogSubmitField
            onSubmit={actions.onSubmitRepeatConfirm}
            onCancel={actions.onCloseRepeatConfirm}
            submitLabel={t('Submit')}
            cancelLabel={t('Cancel')}
            isLoading={state.isLoading}
          />
        }
      >
        <ul>
          <li>
            <Radio
              name="repeatConfirmType"
              isChecked={
                state.repeatConfirmType.repeatConfirm ===
                RepeatConfirm.onlyThisDay
              }
              onChange={actions.onChange}
              label={onlyThisDayText}
              value={RepeatConfirm.onlyThisDay}
            />
          </li>
          <li>
            <Radio
              name="repeatConfirmType"
              isChecked={
                state.repeatConfirmType.repeatConfirm === RepeatConfirm.all
              }
              label={allText}
              onChange={actions.onChange}
              value={RepeatConfirm.all}
            />
          </li>
        </ul>
        <DialogDescription>
          {t('Repeat Confirm Description', {
            every: allText,
          })}
        </DialogDescription>
      </Dialog>
    );
  }, [
    props.editCalendar,
    state.repeatConfirmType,
    state.isLoading,
    props.viewMode,
  ]);

  const FormComponent = (
    <ThemeProvider theme={forceLayoutTheme}>
      <Header>
        <HeaderTitle>{t('title')}</HeaderTitle>
        <HeaderButtonContainer>
          {props.editCalendar && (
            <CloseButtonWrap
              onClick={actions.onClickMenu}
              isDisabled={state.isLoading}
            >
              <CloseIcon name="dots-horizontal" />
            </CloseButtonWrap>
          )}
          <CloseButtonWrap onClick={props.onClose} isDisabled={state.isLoading}>
            <CloseIcon name="close" />
          </CloseButtonWrap>
        </HeaderButtonContainer>
      </Header>

      <Content>
        <Section>
          <FormField>
            <InlineFieldTitle>{t('Started At')}</InlineFieldTitle>
            <DateTimeSelect
              timePicker
              timeZone={props.timeZone}
              date={state.startedAt}
              hour={state.startedAt?.hour}
              minute={state.startedAt?.minute}
              onSelect={actions.onChangeStartedAt}
              isDisabled={state.isLoading}
            />
          </FormField>
          <FormField>
            <InlineFieldTitle>{t('Ended At')}</InlineFieldTitle>
            <DateTimeSelect
              timePicker
              timeZone={props.timeZone}
              date={state.endedAt}
              hour={state.endedAt?.hour}
              minute={state.endedAt?.minute}
              onSelect={actions.onChangeEndedAt}
              isDisabled={state.isLoading}
              selectable24hour
            />
          </FormField>
        </Section>
        {typeof props.editCalendar?.calendar.parentEventTypeCalendarId !==
          'number' && (
          <div>
            <TopAlignFormField>
              <InlineFieldTitle>{t('Schedule')}</InlineFieldTitle>
              <DetailContainer>
                <RepeatSelect
                  name="isRepeat"
                  value={state.isRepeat ? '1' : '0'}
                  options={repeatOptions}
                  onChange={actions.onChange}
                  variant="filled"
                  isDisabled={state.isLoading}
                />
                {state.isRepeat && (
                  <RoutineScheduleFormContainer>
                    <RoutineScheduleForm
                      startedAt={state.startedAt}
                      isAllday={false}
                      schedule={state.schedules}
                      index={0}
                      onSubmit={actions.onSubmitSchedule}
                      deletable={false}
                      isDisabled={state.isLoading}
                      onlyWeekly
                      {...routineScheduleForm.state}
                      {...routineScheduleForm.actions}
                    />
                  </RoutineScheduleFormContainer>
                )}
              </DetailContainer>
            </TopAlignFormField>
            {state.isRepeat && (
              <TopAlignFormField>
                <InlineFieldTitle>{t('End date')}</InlineFieldTitle>
                <DetailContainer>
                  <EndDateSelect
                    name="endDate"
                    value={state.endDate}
                    options={endDateOptions}
                    onChange={actions.onChange}
                    variant="filled"
                    isDisabled={state.isLoading}
                  />
                  {state.endDate === 1 && (
                    <DateTimeSelect
                      timeZone={props.timeZone}
                      date={
                        state.endDateSpecified?.hour === 0
                          ? state.endDateSpecified.minus({ day: 1 })
                          : state.endDateSpecified
                      }
                      hour={state.endDateSpecified?.hour}
                      minute={state.endDateSpecified?.minute}
                      isAllday
                      onSelect={actions.onChangeEndDateSpecified}
                      isDisabled={state.isLoading}
                    />
                  )}
                </DetailContainer>
              </TopAlignFormField>
            )}
          </div>
        )}
        {state.menuAnchor && (
          <FlowSettingMenu
            onCloseMenu={actions.onCloseMenu}
            onSelectMenu={actions.onSelectMenu}
            anchor={state.menuAnchor}
            options={settingMenus}
          />
        )}
        {RepeatConfirmDialogComponent}
        {ConfirmDeleteDialogComponent}
      </Content>
      <Footer>
        {state.isMobile && (
          <CancelButton onClick={props.onClose} size={'large'}>
            {t('Cancel')}
          </CancelButton>
        )}
        <SubmitButton
          size={'large'}
          type="submit"
          color="primary"
          onClick={actions.onSubmit}
          isDisabled={!state.canSubmit}
          isLoading={state.isLoading}
        >
          {t('Submit')}
        </SubmitButton>
      </Footer>
    </ThemeProvider>
  );

  return state.isMobile ? (
    <Modal isOpen>
      <ModalInner>{FormComponent}</ModalInner>
    </Modal>
  ) : (
    <Popover
      anchorReference={state.localPosition ? 'anchorPosition' : 'anchorEl'}
      anchor={state.localPosition ? undefined : props.anchor}
      anchorPosition={state.localPosition ?? undefined}
      transformOrigin={{
        vertical: 'top',
        horizontal: props.isLeftPosition ? 'right' : 'left',
      }}
      onClose={actions.onClosePopover}
      maxWidth={320}
    >
      <PopoverInner>{FormComponent}</PopoverInner>
    </Popover>
  );
}

const ModalInner = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: initial;
  height: 100dvh;
  background-color: ${white};
`;
const PopoverInner = styled.div`
  background-color: ${white};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 57px;
  padding: ${spacing(16)};

  ${mediaMax.smartphone} {
    border-bottom: 1px solid ${gray05};
  }
`;
const HeaderTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const HeaderButtonContainer = styled.div`
  display: flex;
`;
const CloseButtonWrap = styled(IconButton)`
  padding: ${spacing(4)};
  margin-left: ${spacing(4)};
`;
const CloseIcon = styled(Icon)`
  color: ${gray01};
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 57px - 72px);
  overflow: auto;
  padding: 0 ${spacing(16)} ${spacing(16)};

  ${mediaMax.smartphone} {
    padding: ${spacing(16)} ${spacing(16)};
  }
`;

const Section = styled.div`
  padding-bottom: ${spacing(12)};
  margin-bottom: ${spacing(12)};
  border-bottom: 1px solid ${gray04};
  width: 100%;

  &:last-of-type {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }
`;
const FormField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing(8)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const TopAlignFormField = styled(FormField)`
  align-items: flex-start;
`;
const FieldTitle = styled.div`
  display: flex;
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(12)};
`;
const InlineFieldTitle = styled(FieldTitle)`
  height: 32px;
  line-height: 32px;
  margin-right: ${spacing(8)};
  margin-bottom: 0;
`;
const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const SelectWrap = styled(Select)`
  .MuiFilledInput-root {
    background-color: ${gray06};
    font-size: ${fontSize(14)};
  }
  .MuiFilledInput-root:hover {
    background-color: ${gray05};
  }
  .MuiFilledInput-input {
    padding: ${spacing(8)} ${spacing(32)} ${spacing(8)} ${spacing(12)};
  }
  .MuiFilledInput-underline:before {
    border: none;
  }
  .MuiFilledInput-underline:after {
    border: none;
  }
`;
const EndDateSelect = styled(SelectWrap)`
  width: 180px;
  margin-bottom: ${spacing(4)};
`;
const RepeatSelect = styled(SelectWrap)`
  width: 100%;
  min-width: initial;
  margin-bottom: ${spacing(4)};
`;
const RoutineScheduleFormContainer = styled.div`
  width: 100%;
  padding-bottom: ${spacing(12)};
  margin-bottom: ${spacing(12)};
  border-bottom: 1px solid ${gray04};

  :last-of-type {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  padding: ${spacing(16)} 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${white};

  ${mediaMax.smartphone} {
    border-top: 1px solid ${gray04};
  }
`;
const CancelButton = styled(Button)`
  width: 120px;
  min-height: initial;
  height: 40px;
  margin-right: ${spacing(24)};
`;
const SubmitButton = styled(Button)`
  width: 120px;
  min-height: initial;
  height: 40px;
`;

const DialogDescription = styled.div`
  margin-top: ${spacing(24)};
`;
